import React, { useEffect, useState } from "react";
import { adminCheck, adminRecent, adminPreviewTomorrow, adminSendToday, adminPostTweet, getTwitterStats } from "../lib/api";
import { SectionTitle, Loader } from "../components/Shared";
import { Lock, Send, Sparkles, RefreshCw, Mail, BookOpen, ListChecks, AlertCircle, Check, Twitter } from "lucide-react";

const TOKEN_KEY = "edusphere_admin_token_v1";

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [err, setErr] = useState("");
  const [recent, setRecent] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [sending, setSending] = useState(false);
  const [tweetStats, setTweetStats] = useState(null);
  const [tweetResult, setTweetResult] = useState(null);
  const [tweeting, setTweeting] = useState(false);

  const postTweet = async () => {
    setTweeting(true);
    try {
      const r = await adminPostTweet(token);
      setTweetResult(r);
      try { setTweetStats(await getTwitterStats()); } catch (e) {}
    } catch (e) {} finally { setTweeting(false); }
  };

  // auto-try saved token
  useEffect(() => {
    const t = sessionStorage.getItem(TOKEN_KEY);
    if (t) {
      adminCheck(t).then(() => { setAuthed(true); loadRecent(t); })
        .catch(() => { sessionStorage.removeItem(TOKEN_KEY); setAuthed(false); });
    }
  }, []);

  const loadRecent = async (t) => {
    try {
      const r = await adminRecent(t || token);
      setRecent(r);
    } catch (e) { /* ignore */ }
    try { setTweetStats(await getTwitterStats()); } catch (e) {}
  };

  const submitToken = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await adminCheck(token.trim());
      sessionStorage.setItem(TOKEN_KEY, token.trim());
      setAuthed(true);
      loadRecent(token.trim());
    } catch (ex) {
      const status = ex?.response?.status;
      setErr(status === 403 ? "Invalid admin token." : status === 503 ? "Admin token not configured on server." : "Could not authenticate.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setRecent(null);
    setPreview(null);
    setSendResult(null);
  };

  const previewTomorrow = async () => {
    setPreviewLoading(true);
    try {
      const p = await adminPreviewTomorrow(token);
      setPreview(p);
    } catch (e) {} finally { setPreviewLoading(false); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await adminSendToday(token);
      setSendResult(r);
      loadRecent();
    } catch (e) {} finally { setSending(false); }
  };

  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-5 md:px-10 py-12 md:py-20" data-testid="admin-page">
        <div className="card p-8">
          <div className="w-11 h-11 rounded-full bg-[#FBE7DF] grid place-items-center text-[#E07A5F] mb-3">
            <Lock size={20} />
          </div>
          <h1 className="font-heading text-2xl font-bold">Founder admin</h1>
          <p className="text-sm text-[#4A4A4A] mt-2">Enter your <code>ADMIN_TOKEN</code> to continue.</p>
          <form onSubmit={submitToken} className="mt-5 space-y-3" data-testid="admin-token-form">
            <input
              type="password"
              className="input-field"
              placeholder="paste token"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              data-testid="admin-token-input"
              autoFocus
            />
            {err && <div className="text-[#D9534F] text-sm" data-testid="admin-error">{err}</div>}
            <button type="submit" className="btn-primary w-full" data-testid="admin-token-submit">Unlock</button>
          </form>
          <p className="text-xs text-[#4A4A4A] mt-4">Tip: <code>grep ADMIN_TOKEN /app/backend/.env</code> on your server.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-10 py-8 md:py-12" data-testid="admin-page">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-7">
        <div>
          <div className="text-[11px] tracking-[0.22em] uppercase text-[#3A5A40] font-semibold">Founder admin</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Mission control.</h1>
        </div>
        <button onClick={logout} className="btn-outline inline-flex items-center gap-2" data-testid="admin-logout">
          <Lock size={14} /> Lock
        </button>
      </div>

      {/* Action row */}
      <div className="grid md:grid-cols-3 gap-5 mb-8">
        <div className="card p-6" data-testid="admin-action-preview">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#EAECE6] grid place-items-center text-[#3A5A40]"><Sparkles size={18} /></div>
            <div className="font-heading font-bold text-lg">Preview tomorrow</div>
          </div>
          <p className="text-sm text-[#4A4A4A]">Generates the next-day story without caching.</p>
          <button onClick={previewTomorrow} disabled={previewLoading} className="btn-primary mt-4 inline-flex items-center gap-2 disabled:opacity-60" data-testid="admin-preview-btn">
            {previewLoading ? <RefreshCw size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {previewLoading ? "Generating…" : "Generate"}
          </button>
        </div>
        <div className="card p-6" data-testid="admin-action-send">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#FBE7DF] grid place-items-center text-[#E07A5F]"><Send size={18} /></div>
            <div className="font-heading font-bold text-lg">Send digest</div>
          </div>
          <p className="text-sm text-[#4A4A4A]">Fires today's email to active subscribers (mocked if no Resend key).</p>
          <button onClick={sendNow} disabled={sending} className="btn-secondary mt-4 inline-flex items-center gap-2 disabled:opacity-60" data-testid="admin-send-btn">
            {sending ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
            {sending ? "Sending…" : "Send digest"}
          </button>
          {sendResult && (
            <div className="mt-4 text-xs text-[#1A1A1A] bg-[#EAECE6] border border-[#DCD7CE] rounded-xl p-3" data-testid="admin-send-result">
              <div className="flex items-center gap-2 mb-1">
                {sendResult.is_mocked_mode ? <AlertCircle size={12} className="text-[#E07A5F]" /> : <Check size={12} className="text-[#3A5A40]" />}
                <strong>{sendResult.is_mocked_mode ? "Mocked" : "Sent"}</strong>
              </div>
              <div>Attempted: {sendResult.subscribers_attempted} · Sent: {sendResult.sent} · Mocked: {sendResult.mocked} · Errors: {sendResult.errors}</div>
            </div>
          )}
        </div>
        <div className="card p-6" data-testid="admin-action-tweet">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#1A1A1A] grid place-items-center text-white"><Twitter size={18} /></div>
            <div className="font-heading font-bold text-lg">Post to X</div>
          </div>
          <p className="text-sm text-[#4A4A4A]">Tweets today's story with the AI illustration. Idempotent per UTC day.</p>
          {tweetStats && (
            <div className="text-xs text-[#4A4A4A] mt-2" data-testid="admin-tweet-stats">
              {tweetStats.total_posts} posts so far {tweetStats.is_mocked_mode ? "(mock mode)" : "(live)"}
            </div>
          )}
          <button onClick={postTweet} disabled={tweeting} className="mt-4 inline-flex items-center gap-2 bg-[#1A1A1A] hover:bg-[#000] text-white rounded-full px-5 py-2 font-semibold disabled:opacity-60" data-testid="admin-tweet-btn">
            {tweeting ? <RefreshCw size={14} className="animate-spin" /> : <Twitter size={14} />}
            {tweeting ? "Posting…" : "Tweet today"}
          </button>
          {tweetResult && (
            <div className="mt-4 text-xs text-[#1A1A1A] bg-[#EAECE6] border border-[#DCD7CE] rounded-xl p-3" data-testid="admin-tweet-result">
              <div className="flex items-center gap-2 mb-1">
                {tweetResult.is_mocked_mode || tweetResult.status === "mocked" ? <AlertCircle size={12} className="text-[#E07A5F]" /> :
                  tweetResult.status === "error" ? <AlertCircle size={12} className="text-[#D9534F]" /> :
                  <Check size={12} className="text-[#3A5A40]" />}
                <strong>{tweetResult.status}</strong>
              </div>
              {tweetResult.title && <div>{tweetResult.title}</div>}
              {tweetResult.status === "already_posted" && (
                <div className="text-[#4A4A4A] mt-1">Already tweeted today — fires once per UTC day.</div>
              )}
              {tweetResult.tweet_id && <div className="text-[#3A5A40]">Tweet ID: {tweetResult.tweet_id}</div>}
              {tweetResult.error && <div className="text-[#D9534F]">{tweetResult.error}</div>}
            </div>
          )}
        </div>
      </div>

      {/* Preview output */}
      {preview && (
        <div className="card p-7 md:p-8 mb-8 fadeIn" data-testid="admin-preview-output">
          <div className="text-[11px] tracking-[0.22em] uppercase text-[#3A5A40] font-semibold">{preview.date} · {preview.topic}</div>
          <h2 className="font-story text-3xl font-semibold mt-1">{preview.title}</h2>
          <p className="font-story text-[18px] leading-[1.65] mt-4 whitespace-pre-wrap text-[#1A1A1A]">{preview.story}</p>
          <div className="grid md:grid-cols-2 gap-4 mt-6">
            <div className="bg-[#FBE7DF] rounded-2xl p-5 border border-[#DCD7CE]">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#E07A5F] font-semibold mb-2">Moral</div>
              <div className="text-[#1A1A1A]">{preview.moral}</div>
            </div>
            <div className="bg-[#EAECE6] rounded-2xl p-5 border border-[#DCD7CE]">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#3A5A40] font-semibold mb-2">Reflection</div>
              <div className="text-[#1A1A1A]">{preview.reflection_question}</div>
            </div>
          </div>
        </div>
      )}

      {/* Recent panels */}
      {recent && (
        <div className="grid md:grid-cols-3 gap-5">
          <div className="card p-6" data-testid="admin-recent-subs">
            <div className="flex items-center gap-2 mb-4">
              <Mail size={16} className="text-[#E07A5F]" />
              <div className="font-heading font-bold">Recent subscribers</div>
            </div>
            {recent.subscribers.length === 0 ? <div className="text-sm text-[#4A4A4A]">None yet.</div> : (
              <ul className="text-sm space-y-2">
                {recent.subscribers.map((s, i) => (
                  <li key={i} className="border-b border-[#DCD7CE] pb-1.5 last:border-0">
                    <div className="font-medium text-[#1A1A1A]">{s.email || s.phone}</div>
                    <div className="text-xs text-[#4A4A4A]">{s.language} · {s.delivery_hour_ist}:00 IST {s.last_sent_date ? `· last ${s.last_sent_date}` : ""}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="card p-6" data-testid="admin-recent-stories">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen size={16} className="text-[#3A5A40]" />
              <div className="font-heading font-bold">Recent stories</div>
            </div>
            {recent.stories.length === 0 ? <div className="text-sm text-[#4A4A4A]">None yet.</div> : (
              <ul className="text-sm space-y-2">
                {recent.stories.map((s, i) => (
                  <li key={i} className="border-b border-[#DCD7CE] pb-1.5 last:border-0">
                    <div className="font-medium text-[#1A1A1A]">{s.title}</div>
                    <div className="text-xs text-[#4A4A4A]">{s.topic} {s.illustration_url ? "· illustrated" : ""}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="card p-6" data-testid="admin-recent-quizzes">
            <div className="flex items-center gap-2 mb-4">
              <ListChecks size={16} className="text-[#E07A5F]" />
              <div className="font-heading font-bold">Recent quizzes</div>
            </div>
            {recent.quizzes.length === 0 ? <div className="text-sm text-[#4A4A4A]">None yet.</div> : (
              <ul className="text-sm space-y-2">
                {recent.quizzes.map((q, i) => (
                  <li key={i} className="border-b border-[#DCD7CE] pb-1.5 last:border-0">
                    <div className="font-medium text-[#1A1A1A]">{q.topic}</div>
                    <div className="text-xs text-[#4A4A4A]">{q.level} · {q.language}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
